import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { formatPhone, telHref } from '../../shared/phone';
import { formatShortDateTime } from '../../shared/time';
import {
  Button,
  Callout,
  Fact,
  IconArrowRight,
  IconButton,
  IconCheck,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconCopy,
  IconMail,
  IconMessage,
  IconMore,
  IconPhone,
  IconSparkle,
  Menu,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  Panel,
  RailRow,
  Row,
  SectionTitle,
  Skeleton,
  StatusDot,
} from '../../ui/v2';
import { InquiryActivity } from './InquiryActivity';
import { InquiryEditor } from './InquiryEditor';
import { useInquiry, useInquiryActions } from './data';
import type { InquiryKindConfig } from './kind';
import {
  type EditableFields,
  type InquiryRecord,
  type Stage,
  STAGES,
  STAGE_LABEL,
  STAGE_STATE,
  STAGE_TONE,
  closedStageNote,
  daysUntil,
  formatEventDate,
  formatTime,
  nextAction,
  occasionLabel,
  titleFor,
} from './model';

/** Opening a new inquiry marks it seen once the reader has had a moment on it. */
export const MARK_SEEN_DELAY_MS = 1500;

interface Props {
  config: InquiryKindConfig;
  id: number;
  /** The list row, so the header renders before the detail arrives. */
  row: InquiryRecord | null;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  showBack?: boolean;
}

function useAuthorName() {
  const { user } = useAuth();
  if (!user) return undefined;
  return (
    `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email
  );
}

const CopyButton: React.FC<{ value: string; label: string }> = ({
  value,
  label,
}) => {
  const [done, setDone] = useState(false);
  return (
    <IconButton
      size="sm"
      label={done ? 'Copied' : `Copy ${label}`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
          setTimeout(() => setDone(false), 1200);
        } catch {
          /* clipboard unavailable */
        }
      }}
      className={clsx(
        'opacity-0 focus-visible:opacity-100 group-hover:opacity-100',
        done && 'text-v2-success opacity-100',
      )}
    >
      <IconCopy size={14} />
    </IconButton>
  );
};

const StageMenuButton: React.FC<{
  value: Stage;
  onChange: (s: Stage) => void;
  busy?: boolean;
}> = ({ value, onChange, busy }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  return (
    <div className="relative inline-flex">
      <button
        ref={ref}
        type="button"
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Stage: ${STAGE_LABEL[value]}`}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-7 items-center gap-1.5 rounded-v2 border border-v2-line-strong/80 bg-v2-surface pl-2 pr-1.5 text-v2-12 font-medium text-v2-fg hover:bg-v2-sunken disabled:opacity-60"
      >
        <StatusDot tone={STAGE_TONE[value]} />
        {STAGE_LABEL[value]}
        <IconChevronDown size={14} className="text-v2-fg-3" />
      </button>
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={ref}
        width={200}
        className="top-full"
      >
        <MenuLabel>Move to</MenuLabel>
        {STAGES.map((s) => (
          <MenuItem
            key={s}
            selected={s === value}
            onClick={() => {
              setOpen(false);
              if (s !== value) onChange(s);
            }}
            leading={<StatusDot tone={STAGE_TONE[s]} className="mt-1" />}
          >
            <span className="flex items-center justify-between">
              {STAGE_LABEL[s]}
              {s === value && <IconCheck size={14} className="text-v2-fg-3" />}
            </span>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export const InquiryDetail: React.FC<Props> = ({
  config,
  id,
  row,
  onClose,
  onPrev,
  onNext,
  showBack,
}) => {
  const detail = useInquiry(config, id);
  const actions = useInquiryActions(config);
  const authorName = useAuthorName();
  const [editing, setEditing] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [busy, setBusy] = useState<'save' | 'state' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const autoSeen = useRef(false);
  const seenTimer = useRef<number | null>(null);

  const e = detail.data ?? row;
  const stage = detail.data?.stage;

  const clearSeenTimer = () => {
    if (seenTimer.current === null) return;
    window.clearTimeout(seenTimer.current);
    seenTimer.current = null;
  };

  useEffect(() => {
    if (stage !== 'new' || autoSeen.current) return undefined;
    seenTimer.current = window.setTimeout(() => {
      seenTimer.current = null;
      autoSeen.current = true;
      actions.setState(id, 'seen').catch(() => undefined);
    }, MARK_SEEN_DELAY_MS);
    return clearSeenTimer;
  }, [id, stage, actions]);

  const run = async (kind: 'save' | 'state', fn: () => Promise<unknown>) => {
    setBusy(kind);
    setError(null);
    try {
      await fn();
      return true;
    } catch (err) {
      setError((err as Error).message);
      return false;
    } finally {
      setBusy(null);
    }
  };
  const moveTo = (s: Stage) => {
    // A deliberate move sticks. Cancel a mark-seen that is already waiting,
    // or it can land after this write and put the inquiry back to seen.
    clearSeenTimer();
    autoSeen.current = true;
    return run('state', () => actions.setState(id, STAGE_STATE[s]));
  };

  const panel = (title: React.ReactNode, children: React.ReactNode) => (
    <Panel
      onClose={onClose}
      showBack={showBack}
      backLabel={config.title}
      ariaLabel={
        e ? `${config.recordLabel} inquiry from ${titleFor(e)}` : 'Inquiry'
      }
      title={title}
      actions={
        <>
          <div className="mr-1 hidden items-center sm:flex">
            <IconButton
              size="sm"
              label="Previous inquiry"
              onClick={onPrev}
              disabled={!onPrev}
            >
              <IconChevronLeft />
            </IconButton>
            <IconButton
              size="sm"
              label="Next inquiry"
              onClick={onNext}
              disabled={!onNext}
            >
              <IconChevronRight />
            </IconButton>
          </div>
          {detail.data && (
            <div className="relative">
              <IconButton
                ref={moreRef}
                size="sm"
                label="More actions"
                onClick={() => setMoreOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={moreOpen}
              >
                <IconMore />
              </IconButton>
              <Menu
                open={moreOpen}
                onClose={() => setMoreOpen(false)}
                anchorRef={moreRef}
                align="right"
                width={200}
              >
                <MenuItem
                  onClick={() => {
                    setMoreOpen(false);
                    setEditing(true);
                  }}
                >
                  Edit details
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setMoreOpen(false);
                    void navigator.clipboard
                      ?.writeText(window.location.href)
                      .catch(() => undefined);
                  }}
                >
                  Copy link
                </MenuItem>
                <MenuSeparator />
                {detail.data.stage !== 'new' && (
                  <MenuItem
                    onClick={() => {
                      setMoreOpen(false);
                      void moveTo('new');
                    }}
                  >
                    Mark as unread
                  </MenuItem>
                )}
                {detail.data.stage === 'lost' ||
                detail.data.stage === 'won' ||
                detail.data.stage === 'archived' ? (
                  <MenuItem
                    onClick={() => {
                      setMoreOpen(false);
                      void moveTo('to_contact');
                    }}
                  >
                    Reopen
                  </MenuItem>
                ) : (
                  <>
                    <MenuItem
                      onClick={() => {
                        setMoreOpen(false);
                        void moveTo('won');
                      }}
                    >
                      Mark as won
                    </MenuItem>
                    <MenuItem
                      tone="danger"
                      onClick={() => {
                        setMoreOpen(false);
                        void moveTo('lost');
                      }}
                    >
                      Mark as lost
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setMoreOpen(false);
                        void moveTo('archived');
                      }}
                    >
                      Archive
                    </MenuItem>
                  </>
                )}
              </Menu>
            </div>
          )}
        </>
      }
    >
      {children}
    </Panel>
  );

  const idTitle = (
    <span className="v2-num text-v2-12 text-v2-fg-3">
      {config.recordLabel} <span className="text-v2-fg-2">#{id}</span>
    </span>
  );

  if (detail.isError && !detail.data) {
    return panel(
      idTitle,
      <div className="p-6">
        <Callout tone="danger" title="Couldn’t load this inquiry">
          {detail.error.message}
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={() => void detail.refetch()}>
              Retry
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}>
              Back to {config.title}
            </Button>
          </div>
        </Callout>
      </div>,
    );
  }

  if (!e) return panel(idTitle, <DetailSkeleton />);

  const na = nextAction(e);
  const closedNote = closedStageNote(e.stage);
  const days = daysUntil(e.eventDate);
  const missing = e.missingInformation;
  const loaded = !!detail.data;

  return panel(
    idTitle,
    <div className="v2-scroll min-h-0 flex-1 overflow-y-auto">
      <div className="max-w-[1160px] px-4 pt-5 lg:px-6">
        <StageMenuButton
          value={e.stage}
          onChange={(s) => void moveTo(s)}
          busy={busy === 'state' || !loaded}
        />
        <h2 className="mt-3 text-v2-20 font-semibold tracking-[-0.01em] text-v2-fg">
          {titleFor(e)}
          {!e.guestName && (
            <span className="ml-2 align-middle text-v2-12 font-normal text-v2-fg-3">
              name not captured
            </span>
          )}
        </h2>
        <p className="mt-0.5 text-v2-13 text-v2-fg-2">
          {occasionLabel(e.occasion, config.occasionFallback)}
        </p>

        <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-y border-v2-line py-3 sm:grid-cols-4">
          <Fact
            label="Date"
            value={
              e.eventDate || e.eventDateRaw
                ? formatEventDate(e.eventDate, e.eventDateRaw, {
                    weekday: true,
                  })
                : undefined
            }
            sub={
              days != null && days >= 0 && e.stage !== 'lost'
                ? days === 0
                  ? 'today'
                  : `in ${days}d`
                : undefined
            }
            missing={!e.eventDate && !e.eventDateRaw}
          />
          <Fact
            label="Time"
            value={e.eventTime ? formatTime(e.eventTime) : undefined}
            missing={!e.eventTime}
          />
          <Fact
            label={config.labels.guests}
            value={e.guestCount ?? e.guestCountRaw ?? undefined}
            missing={e.guestCount == null && !e.guestCountRaw}
          />
          <Fact
            label="Budget"
            value={e.budget ?? undefined}
            missing={!e.budget}
          />
        </dl>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p
            className={clsx(
              'inline-flex items-center gap-1.5 text-v2-13',
              na.urgency === 'now' ? 'font-medium text-v2-fg' : 'text-v2-fg-2',
            )}
          >
            {na.label ? (
              <>
                <IconArrowRight
                  size={14}
                  className={
                    na.urgency === 'now' ? 'text-v2-warning' : 'text-v2-fg-3'
                  }
                />
                {na.label}
              </>
            ) : (
              closedNote && <span className="text-v2-fg-3">{closedNote}</span>
            )}
          </p>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              leading={<IconPhone size={14} />}
              disabledReason={e.phone ? undefined : 'No phone number captured'}
              onClick={() => {
                if (e.phone) window.location.href = telHref(e.phone)!;
              }}
            >
              Call
            </Button>
            <Button
              size="sm"
              leading={<IconMessage size={14} />}
              disabledReason={e.phone ? undefined : 'No phone number captured'}
              onClick={() => {
                if (e.phone) window.location.href = `sms:${e.phone}`;
              }}
            >
              Text
            </Button>
            <Button
              size="sm"
              leading={<IconMail size={14} />}
              disabledReason={e.email ? undefined : 'No email address captured'}
              onClick={() => {
                if (e.email) window.location.href = `mailto:${e.email}`;
              }}
            >
              Email
            </Button>
          </div>
        </div>
        {(!e.phone || !e.email) && (
          <p className="mt-1 text-right text-v2-12 text-v2-fg-3">
            {!e.phone && !e.email
              ? 'No phone number or email was captured.'
              : !e.phone
                ? 'No phone number was captured.'
                : 'No email address was captured.'}
          </p>
        )}
        {error && (
          <Callout
            tone="danger"
            className="mt-3"
            title="That didn’t go through"
          >
            {error}
          </Callout>
        )}
      </div>

      <div className="grid max-w-[1160px] grid-cols-1 gap-x-8 px-4 pb-8 pt-6 lg:px-6 2xl:grid-cols-[minmax(0,1fr)_248px] 2xl:grid-rows-[auto_1fr]">
        <div className="min-w-0 2xl:col-start-1 2xl:row-start-1">
          <section aria-labelledby="inq-summary">
            <SectionTitle>
              <span
                id="inq-summary"
                className="inline-flex items-center gap-1.5"
              >
                <IconSparkle size={13} className="text-v2-accent" /> Arthur’s
                summary
              </span>
            </SectionTitle>
            {e.summary ? (
              <p className="mt-1.5 max-w-[68ch] whitespace-pre-wrap text-v2-13 leading-5 text-v2-fg">
                {e.summary}
              </p>
            ) : (
              <p className="mt-1.5 text-v2-13 text-v2-fg-3">
                {e.coordinatorEmail.status === 'pending' ||
                e.coordinatorEmail.status === 'processing'
                  ? 'Arthur is still processing this call.'
                  : 'No summary was saved for this inquiry.'}
              </p>
            )}
            {missing.length > 0 && e.stage !== 'lost' && (
              <Callout
                tone="warning"
                className="mt-3"
                title={`Arthur couldn’t capture ${missing.length === 1 ? 'one detail' : `${missing.length} details`}`}
              >
                {missing.join(' · ')}
                <span className="text-v2-fg-3"> — ask when you reach out.</span>
              </Callout>
            )}
            {e.coordinatorEmail.status === 'failed' && (
              <Callout
                tone="danger"
                className="mt-3"
                title="The team email for this inquiry failed to send"
              >
                {e.coordinatorEmail.error ||
                  'Check the notification recipients in Store info.'}
              </Callout>
            )}
          </section>

          <section className="mt-7" aria-labelledby="inq-details">
            <SectionTitle
              action={
                !editing &&
                loaded && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditing(true)}
                  >
                    Edit
                  </Button>
                )
              }
            >
              <span id="inq-details">{config.recordLabel} details</span>
            </SectionTitle>
            {editing && detail.data ? (
              <div className="mt-2 rounded-v2-lg border border-v2-line p-4">
                <InquiryEditor
                  config={config}
                  record={detail.data}
                  submitting={busy === 'save'}
                  onCancel={() => setEditing(false)}
                  onSubmit={async (patch: EditableFields) => {
                    const ok = await run('save', () =>
                      actions.update(id, patch),
                    );
                    if (ok) setEditing(false);
                  }}
                />
              </div>
            ) : (
              <DetailsList config={config} e={e} />
            )}
          </section>
        </div>

        <aside className="mt-7 min-w-0 2xl:col-start-2 2xl:row-span-2 2xl:row-start-1 2xl:mt-0">
          <Rail e={e} />
        </aside>

        <section
          className="mt-7 min-w-0 2xl:col-start-1 2xl:row-start-2"
          aria-labelledby="inq-activity"
        >
          <SectionTitle>
            <span id="inq-activity">Activity</span>
          </SectionTitle>
          <div className="mt-3">
            {loaded ? (
              <InquiryActivity
                items={e.activity}
                guestName={e.guestName}
                authorName={authorName}
                onAddNote={(text) =>
                  actions.addNote(id, text, authorName || 'Team')
                }
              />
            ) : (
              <Skeleton className="h-3" width="60%" />
            )}
          </div>
        </section>
      </div>
    </div>,
  );
};

const DetailsList: React.FC<{
  config: InquiryKindConfig;
  e: InquiryRecord;
}> = ({ config, e }) => (
  <dl className="mt-1">
    <Row label={config.labels.occasion} empty={!e.occasion}>
      {e.occasion ? occasionLabel(e.occasion, '') : undefined}
    </Row>
    <Row label="Date" empty={!e.eventDate && !e.eventDateRaw}>
      {e.eventDate ? (
        <>
          {formatEventDate(e.eventDate, e.eventDateRaw, {
            weekday: true,
            withYear: true,
          })}
          {e.eventDateRaw && e.eventDateRaw !== e.eventDate && (
            <span className="ml-2 text-v2-12 text-v2-fg-3">
              heard as “{e.eventDateRaw}”
            </span>
          )}
        </>
      ) : e.eventDateRaw ? (
        <>
          <span className="text-v2-fg-2">“{e.eventDateRaw}”</span>
          <span className="ml-2 text-v2-12 text-v2-warning">
            needs a firm date
          </span>
        </>
      ) : undefined}
    </Row>
    <Row label="Time" empty={!e.eventTime}>
      {e.eventTime ? formatTime(e.eventTime) : undefined}
    </Row>
    <Row
      label={config.labels.guestCount}
      empty={e.guestCount == null && !e.guestCountRaw}
    >
      {e.guestCount != null ? (
        <>
          <span className="v2-num">{e.guestCount}</span>
          {e.guestCountRaw && String(e.guestCount) !== e.guestCountRaw && (
            <span className="ml-2 text-v2-12 text-v2-fg-3">
              heard as “{e.guestCountRaw}”
            </span>
          )}
        </>
      ) : (
        (e.guestCountRaw ?? undefined)
      )}
    </Row>
    <Row label="Budget" empty={!e.budget}>
      {e.budget ? (
        <>
          {e.budget}
          <span className="ml-2 text-v2-12 text-v2-fg-3">
            from the call, not editable
          </span>
        </>
      ) : undefined}
    </Row>
    <Row label="Special requests" empty={!e.specialRequests}>
      {e.specialRequests && (
        <span className="whitespace-pre-wrap">{e.specialRequests}</span>
      )}
    </Row>
    <Row label="Preferred contact" empty={!e.preferredContact}>
      {e.preferredContact
        ? e.preferredContact.charAt(0).toUpperCase() +
          e.preferredContact.slice(1)
        : undefined}
    </Row>
    {e.extraFields.map((f) => (
      <Row key={f.key} label={f.label}>
        <span className="whitespace-pre-wrap">{f.value}</span>
      </Row>
    ))}
  </dl>
);

const Rail: React.FC<{ e: InquiryRecord }> = ({ e }) => (
  <div className="flex flex-col gap-6">
    <section aria-labelledby="inq-contact">
      <SectionTitle>
        <span id="inq-contact">Contact</span>
      </SectionTitle>
      <dl className="mt-1 flex flex-col">
        <div className="group flex h-8 items-center justify-between gap-2 border-b border-v2-line/70">
          <dt className="sr-only">Phone</dt>
          <dd className="flex min-w-0 items-center gap-2 text-v2-13">
            <IconPhone size={14} className="shrink-0 text-v2-fg-3" />
            {e.phone ? (
              <a
                href={telHref(e.phone)}
                className="v2-num truncate text-v2-fg hover:underline"
              >
                {formatPhone(e.phone)}
              </a>
            ) : (
              <span className="text-v2-fg-4">No phone</span>
            )}
          </dd>
          {e.phone && <CopyButton value={e.phone} label="phone" />}
        </div>
        <div className="group flex h-8 items-center justify-between gap-2 border-b border-v2-line/70">
          <dt className="sr-only">Email</dt>
          <dd className="flex min-w-0 items-center gap-2 text-v2-13">
            <IconMail size={14} className="shrink-0 text-v2-fg-3" />
            {e.email ? (
              <a
                href={`mailto:${e.email}`}
                className="truncate text-v2-fg hover:underline"
              >
                {e.email}
              </a>
            ) : (
              <span className="text-v2-fg-4">No email</span>
            )}
          </dd>
          {e.email && <CopyButton value={e.email} label="email" />}
        </div>
        {e.preferredContact && (
          <div className="flex h-8 items-center gap-2 text-v2-12 text-v2-fg-3">
            Prefers {e.preferredContact}
          </div>
        )}
      </dl>
    </section>

    <section aria-labelledby="inq-source">
      <SectionTitle>
        <span id="inq-source">Source</span>
      </SectionTitle>
      <dl className="mt-1">
        <RailRow label="Channel">
          {e.source === 'call' ? 'Phone call to Arthur' : 'Submitted inquiry'}
        </RailRow>
        <RailRow label="Received">
          <span className="v2-num">{formatShortDateTime(e.createdAt)}</span>
        </RailRow>
        {e.callerPhone && e.callerPhone !== e.phone && (
          <RailRow label="Called from">
            <span className="v2-num">{formatPhone(e.callerPhone)}</span>
          </RailRow>
        )}
        <RailRow label="Team email">
          {e.coordinatorEmail.status === 'sent' ? (
            <span>
              Sent{' '}
              <span className="v2-num text-v2-fg-3">
                {formatShortDateTime(e.coordinatorEmail.sentAt)}
              </span>
            </span>
          ) : e.coordinatorEmail.status === 'failed' ? (
            <span className="text-v2-danger">Failed</span>
          ) : (
            <span className="text-v2-fg-3">
              {e.coordinatorEmail.status === 'processing'
                ? 'Sending…'
                : 'Not sent'}
            </span>
          )}
        </RailRow>
        {e.coordinatorEmail.recipients.length > 0 && (
          <RailRow label="Sent to">
            <span title={e.coordinatorEmail.recipients.join(', ')}>
              {e.coordinatorEmail.recipients.join(', ')}
            </span>
          </RailRow>
        )}
        {e.retellCallId && (
          <RailRow label="Call ID">
            <span className="v2-num text-v2-fg-3" title={e.retellCallId}>
              {e.retellCallId}
            </span>
          </RailRow>
        )}
      </dl>
    </section>
  </div>
);

const DetailSkeleton: React.FC = () => (
  <div aria-label="Loading inquiry" className="px-4 pt-5 lg:px-6">
    <Skeleton className="h-3 w-20" />
    <Skeleton className="mt-3 block h-5 w-64" />
    <Skeleton className="mt-2 block h-3 w-96" />
    <div className="mt-6 grid grid-cols-4 gap-4 border-y border-v2-line py-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i}>
          <Skeleton className="h-2.5 w-12" />
          <Skeleton className="mt-2 block h-3.5 w-20" />
        </div>
      ))}
    </div>
    <Skeleton className="mt-6 block h-3 w-full" />
    <Skeleton className="mt-2 block h-3 w-[92%]" />
  </div>
);
